import { useCallback, useEffect, useState } from "react";
import { Pin, Trash2, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { fmt } from "@/lib/dates";
import { PageHeader, Panel, Loader, Avatar, Empty, RoleBadge } from "@/components/common";
import { AiBoardWriter } from "@/components/AiBoardWriter";

const CATS = {
  algemeen: { label: "Algemeen", cls: "bg-slate-100 text-slate-700" },
  belangrijk: { label: "Belangrijk", cls: "bg-[#FFECE8] text-[#C2391B]" },
  dienst_ruilen: { label: "Dienst ruilen", cls: "bg-violet-100 text-violet-800" },
};

export default function Board() {
  const api = useApi();
  const { isManager } = useMember();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [cat, setCat] = useState("algemeen");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/messages").then((r) => setMsgs(r.data)), [api]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/messages", { text, category: cat });
      setText("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="board-page">
      <PageHeader title="Prikbord" subtitle="Mededelingen voor het hele team." />
      <Panel className="fade-up" data-testid="message-composer">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Schrijf een bericht voor je team…" rows={3} className="resize-none" data-testid="message-input" />
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
          <div className="flex flex-wrap gap-2">
            {Object.entries(CATS).map(([k, v]) => (
              <button key={k} onClick={() => setCat(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${cat === k ? `${v.cls} ring-2 ring-offset-1 ring-[#0D5C46]` : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`message-category-${k}`}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
          {isManager && <AiBoardWriter onText={setText} />}
          <Button onClick={post} disabled={busy || !text.trim()} className="rounded-full" data-testid="message-post-button"><Send className="h-4 w-4 mr-2" />Plaatsen</Button>
          </div>
        </div>
      </Panel>

      <div className="mt-5 space-y-3" data-testid="message-list">
        {!msgs ? <Loader /> : msgs.length === 0 ? <Empty icon={MessageSquare} title="Nog geen berichten" text="Plaats het eerste bericht voor je team." /> : msgs.map((m) => (
          <Panel key={m.id} className={`fade-up ${m.pinned ? "border-[#FF5A36]/40 bg-[#FFF8F6]" : ""}`} data-testid={`message-${m.id}`}>
            <div className="flex items-start gap-3">
              <Avatar name={m.author_name} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold">{m.author_name}</p>
                  <RoleBadge role={m.author_role} />
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${CATS[m.category].cls}`}>{CATS[m.category].label}</span>
                  {m.pinned && <span className="inline-flex items-center text-[11px] font-semibold text-[#C2391B]"><Pin className="h-3 w-3 mr-1" />Vastgezet</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{fmt(m.created_at, "EEEE d MMM 'om' HH:mm")}</p>
                <p className="mt-2 text-slate-800 whitespace-pre-wrap break-words" data-testid={`message-text-${m.id}`}>{m.text}</p>
              </div>
              <div className="flex gap-1">
                {isManager && (
                  <Button size="icon" variant="ghost" onClick={() => act(() => api.post(`/messages/${m.id}/pin`))} data-testid={`message-pin-${m.id}`} aria-label="Vastzetten">
                    <Pin className={`h-4 w-4 ${m.pinned ? "text-[#FF5A36]" : ""}`} />
                  </Button>
                )}
                {m.can_delete && (
                  <Button size="icon" variant="ghost" onClick={() => window.confirm("Dit bericht verwijderen?") && act(() => api.delete(`/messages/${m.id}`), "Bericht verwijderd")} data-testid={`message-delete-${m.id}`} aria-label="Verwijderen">
                    <Trash2 className="h-4 w-4 text-slate-500" />
                  </Button>
                )}
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
